/**
 * ReportePublicoPage — `/r/:token` (FUERA de ProtectedRoute).
 *
 * El reporte comercial compartible por link, sin login ni chrome de dashboard.
 * Trae los datos REALES del backend público (`GET /activa/public/report/:token`),
 * que sólo expone agregados (marca, report, grupos) — nunca PII.
 * Uno de los tres caminos del PRD (pantalla · PDF · link).
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle, TrendingUp, Users, DollarSign } from "lucide-react";
import ReporteComercial from "../components/ReporteComercial";
import { activaApi } from "../api";

const money = (n: number) =>
  "$" + Math.round(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });

/** El "después": el resultado medido de la campaña. Es la prueba social del piloto — lo primero
 *  que ve el prospecto cuando le compartís el link. Sólo agregados, sin PII.
 *
 *  La plata es lo que compraron los que la recibieron después del envío (`measure`), SIN grupo de control: incluye a
 *  los que habrían comprado igual. Decía «$X recuperados» y «Recuperado»; ahora dice lo que es y lo aclara a la vista
 *  (decisión de Alan del 29/10). Este link es de una campaña de Reactivación: el resultado contra un control (el de
 *  una segmentación, `envios/services/resultado.service.ts`) no pasa por acá. */
function ResultadoPiloto({ r }: { r: any }) {
  const t = r?.total || {};
  const medida = !!r?.medida;
  return (
    <section className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 md:p-8 mb-8 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center"><TrendingUp size={17} /></span>
        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">El resultado de la campaña</p>
      </div>
      {medida ? (
        <>
          <p className="text-lg md:text-xl font-bold text-slate-900 mb-5 leading-snug">
            {(t.volvieron ?? 0) === 1 ? "Volvió" : "Volvieron"} a comprar <span className="text-emerald-600">{t.volvieron ?? 0}</span> {(t.volvieron ?? 0) === 1 ? "persona" : "personas"} después de recibir la campaña
            {typeof t.plataRecuperada === "number" && t.plataRecuperada > 0 && <> · <span className="text-emerald-600">{money(t.plataRecuperada)}</span> en compras</>}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Users, label: "Contactados", val: t.enviados ?? 0 },
              { icon: TrendingUp, label: "Entregados", val: t.entregados ?? 0 },
              { icon: TrendingUp, label: "Leídos", val: t.leidos ?? 0 },
              { icon: DollarSign, label: "Facturaron los que la recibieron", val: typeof t.plataRecuperada === "number" ? money(t.plataRecuperada) : "—" },
            ].map((k, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-100 p-3.5">
                <p className="text-xl font-bold tabular-nums text-slate-900 leading-none">{k.val}</p>
                <p className="text-[11px] text-slate-500 mt-1">{k.label}</p>
              </div>
            ))}
          </div>
          <p data-testid="aclaracion-sin-control" className="text-xs text-slate-500 mt-4 leading-relaxed">
            Incluye a quienes habrían comprado igual: esta campaña no se compara contra un grupo que no la recibió.
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-600 leading-relaxed">
          La campaña ya salió. La medición de cuántos volvieron a comprar se completa con la base del período siguiente.
        </p>
      )}
      {r?.hayDatosSimulados && (
        <p className="text-[11px] text-amber-700 mt-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {r.todoDePrueba
            ? "Estos envíos fueron de prueba (sin canal real conectado): todavía no le llegó nada a ningún cliente."
            : "Parte de los envíos fueron de prueba (sin canal real conectado): no cuentan como entregas reales."}
        </p>
      )}
    </section>
  );
}

const ACCENTS: Record<string, any> = {
  g_dormidos: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", dot: "bg-violet-500" },
  g_campeones: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500" },
  // H76: sin acento propio caía en el naranja de «En riesgo», justo el grupo del que se separó
  g_activos: { bg: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200", dot: "bg-cyan-500" },
  g_nuevos: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  g_riesgo: { bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200", dot: "bg-orange-500" },
};
const withAccent = (grupos: any[]) => (grupos || []).map((g) => ({ ...g, accent: ACCENTS[g.id] || ACCENTS.g_riesgo }));

export default function ReportePublicoPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await activaApi.publicReport(token || "");
        if (vivo) setData(d);
      } catch (e: any) {
        if (vivo) setError(e?.status === 404 ? "Este reporte no existe o el link ya no es válido." : "No pudimos cargar el reporte.");
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const reporteFront = data && {
    marca: data.marca,
    plataDormida: data.report?.plataDormida,
    concentracion: data.report?.concentracion,
    baseDormida: data.report?.baseDormida,
    totalClientes: data.report?.totalClientes,
    rotativo: data.report?.rotativo,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Barra superior mínima */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-4xl px-4 md:px-6 h-14 flex items-center justify-between">
          <span className="text-sm font-bold text-violet-700">DEENEX Activa</span>
          <span className="text-xs text-slate-400">Reporte comercial</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-8">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <Loader2 size={28} className="animate-spin" />
            <p className="text-sm mt-3">Cargando reporte…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center"><AlertCircle size={22} /></div>
            <p className="text-sm font-semibold text-slate-700 mt-4">{error}</p>
            <p className="text-xs text-slate-400 mt-1">Pedile a tu contacto de DEENEX un link actualizado.</p>
          </div>
        )}

        {/* El "después" primero: cuando la campaña ya se midió, el resultado es lo que vende. */}
        {!loading && !error && data?.resultados && <ResultadoPiloto r={data.resultados} />}

        {!loading && !error && reporteFront && (
          <ReporteComercial
            reporte={reporteFront as any}
            /* mismas métricas reales que ve el operador: el link público sólo muestra agregados,
               nunca PII, así que no hay nada que recortar acá */
            real={data.report}
            grupos={withAccent(data.grupos)}
            sinContacto={data.report?.sinContacto}
            publico
          />
        )}
      </main>
    </div>
  );
}
